// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import { PRODUCT_FIELD_TYPE_DATA } from '@/constants/product-field-type';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { ItemField } from '@/items/api/types';
import { updateItemField } from '@/items/api/update-item-field';
import { DeleteItemFieldDialog } from '@/items/components/delete-item-field-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Suspense, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ name: z.string().min(1, 'Name is required') });

type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  isOpen: boolean;

  /**
   * The key of the item containing the field.
   */
  itemKey: string;

  /**
   * The field being updated.
   */
  field: ItemField;

  /**
   * Callback fired when the dialog should close.
   */
  onClose: () => void;
}

/**
 * A dialog component for updating and managing item fields.
 */
export function UpdateItemFieldDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const confirmationDialog = useDisclosure();

  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );
  const [showSnackbar, setShowSnackbar] = useState(false);

  const { formState, reset, register, handleSubmit, setError, clearErrors } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: { name: props.field.label },
    });

  const timeout = useTimeout(reset, theme.transitions.duration.standard);

  useEffect(() => {
    if (props.isOpen) {
      setInlineErrorMessage(null);
      setShowSnackbar(false);
      clearErrors();
    }
  }, [props.isOpen, clearErrors]);

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      return updateItemField(props.itemKey, props.field.key, {
        label: data.name,
      });
    },
    onError: (error: Error) => {
      clearErrors();
      setInlineErrorMessage(null);
      setShowSnackbar(false);
      if (!(error instanceof AxiosError)) {
        captureException(error);
        setShowSnackbar(true);
        return;
      }
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'UNIQUE_VIOLATION') {
        setError('name', {
          type: 'manual',
          message: 'A field with this name already exists',
        });
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setInlineErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSnackbar(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['item', props.itemKey],
      });

      close();
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    mutation.mutate(data);
  };

  const close = () => {
    setInlineErrorMessage(null);
    setShowSnackbar(false);
    timeout.start();
    props.onClose();
  };

  return (
    <>
      <Suspense>
        <DeleteItemFieldDialog
          isOpen={confirmationDialog.isOpen}
          field={props.field}
          itemKey={props.itemKey}
          onClose={confirmationDialog.close}
        />
      </Suspense>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        transitionDuration={theme.transitions.duration.standard}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <SupportSnackbar
          isOpen={showSnackbar}
          message="An error occurred while updating the field."
          onClose={() => setShowSnackbar(false)}
        />
        <DialogTitle>Update field</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {inlineErrorMessage && (
              <Alert
                severity="error"
                onClose={() => setInlineErrorMessage(null)}
              >
                {inlineErrorMessage}
              </Alert>
            )}
            <TextField
              size="small"
              label="Name"
              error={Boolean(formState.errors.name)}
              helperText={formState.errors.name?.message}
              InputProps={register('name')}
            />
            <TextField
              disabled
              size="small"
              label="Type"
              value={PRODUCT_FIELD_TYPE_DATA[props.field.type].fieldTypeLabel}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={confirmationDialog.open}
            sx={{ marginRight: 'auto' }}
          >
            Delete
          </Button>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={mutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
